import { useState, useEffect } from "react";
import validator from "validator";
import { Link, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { FiMail, FiLock, FiUser } from "react-icons/fi";

import IconSpace1 from "../../assets/spaces-icons/1.png";
import IconSpace2 from "../../assets/spaces-icons/2.png";

import { setUser } from "../../redux/auth/actions";

import api from "../../services/api";
import Loader from "../../components/loader";
import AppInfo from "./appInfo";

const Invite = () => {
  const [values, setValues] = useState({ email: "", password: "", name: "" });
  const [errors, setErrors] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.Auth.user);
  const navigate = useNavigate();
  const redirect = searchParams.get("redirect");
  const inviteToken = searchParams.get("token");
  const application = searchParams.get("application") || "accounting";

  async function getUser() {
    try {
      const { ok, user, code } = await api.post("/user/check-invitation-token", { invitation_token: inviteToken });
      if (!ok) throw new Error(code);
      setValues({ ...values, email: user.email });
    } catch (error) {
      console.error(error);
      toast.error("Invalid or expired invitation token");
    }
  }

  useEffect(() => {
    if (inviteToken) getUser();
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!values.email) return setErrors({ ...errors, email: "This field is Required" });
    if (!values.password) return setErrors({ ...errors, password: "This field is Required" });
    if (!values.first_name) return setErrors({ ...errors, first_name: "This field is Required" });
    if (!values.last_name) return setErrors({ ...errors, last_name: "This field is Required" });
    if (!validator.isEmail(values?.email)) return toast.error("Invalid email");

    setLoading(true);
    try {
      const data = { ...values, name: `${values.first_name} ${values.last_name}`, invitation_token: inviteToken };
      delete data.first_name;
      delete data.last_name;

      const { ok, data: user, token, code } = await api.post("/user/invite-accepted", data);
      if (!ok) toast.error(code);
      if (token) api.setToken(token);
      if (user) {
        dispatch(setUser(user));
        toast.success("Your account has been created successfully!");
        if (redirect) navigate(redirect);
        else navigate("/");
      }
    } catch (error) {
      console.log("✌️  error", error);
      if (error.code === "USER_ALREADY_REGISTERED") return toast.error("The email is already registered.\nTry login instead.");
      toast.error("Error while signing up");
    }
    setLoading(false);
  };

  if (user) return <Navigate to={"/"} />;

  if (!redirect && !inviteToken) {
    toast.error("Invalid invitation link");
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 to-gray-900 flex items-center justify-center">
        <div className="relative max-w-xl w-full">
          <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-xl border border-blue-500/20 shadow-2xl p-10">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center">
                  <img src={IconSpace1} alt="Selego" className="w-16 h-16" />
                </div>
              </div>
            </div>
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-white">Invitation</h2>
            </div>
            <div className="mt-6 p-6 bg-gray-800/50 rounded-lg border border-gray-700 text-center">
              <p className="text-gray-300 mt-2 font-medium">You've got an invalid invitation link.</p>
              <p className="text-gray-400 mt-2 text-sm">👉 Please contact the project owner to get a new one</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-gray-900 flex items-center justify-center">
      <div className="relative max-w-xl w-full">
        <div className="relative bg-gray-900/80 backdrop-blur-sm rounded-xl border border-blue-500/20 shadow-2xl p-10">
          <form className="w-full" onSubmit={handleSubmit}>
            <AppInfo application={application} />

            <div className="space-y-6">
              <div>
                <label className="text-base text-gray-300 flex items-center mb-2" htmlFor="name">
                  <FiUser className="mr-2 text-blue-400" size={20} />
                  Name <span className="text-blue-400 ml-1">*</span>
                </label>
                <div className="flex items-center gap-2 w-full">
                  <input
                    className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all text-white text-sm"
                    name="first_name"
                    type="text"
                    value={values.first_name}
                    onChange={(e) => setValues({ ...values, first_name: e.target.value })}
                    placeholder="Enter your first name"
                  />
                  <input
                    className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all text-white text-sm"
                    name="last_name"
                    type="text"
                    value={values.last_name}
                    onChange={(e) => setValues({ ...values, last_name: e.target.value })}
                    placeholder="Enter your surname"
                  />
                </div>
                <div className="flex flex-col">
                  {errors.first_name && <p className="text-xs text-red-400 mt-1">{errors.first_name}</p>}
                  {errors.last_name && <p className="text-xs text-red-400 mt-1">{errors.last_name}</p>}
                </div>
              </div>
              <div>
                <label className="text-base text-gray-300 flex items-center mb-2" htmlFor="email">
                  <FiMail className="mr-2 text-blue-400" size={20} />
                  E-mail <span className="text-blue-400 ml-1">*</span>
                </label>
                <input
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-75 transition-all text-white text-base"
                  validate={(v) => !validator.isEmail(v) && "Invalid email address"}
                  name="email"
                  type="email"
                  id="email"
                  value={values.email}
                  onChange={(e) => setValues({ ...values, email: e.target.value })}
                  placeholder="example@mail.com"
                  disabled
                />
                {errors.email && <p className="text-sm text-red-400 mt-2">{errors.email}</p>}
              </div>
              <div>
                <label className="text-base text-gray-300 flex items-center mb-2" htmlFor="password">
                  <FiLock className="mr-2 text-blue-400" size={20} />
                  Password <span className="text-blue-400 ml-1">*</span>
                </label>
                <input
                  className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-white text-base"
                  name="password"
                  type="password"
                  id="password"
                  value={values.password}
                  onChange={(e) => setValues({ ...values, password: e.target.value })}
                  placeholder="Choose a secure password"
                />
                {errors.password && <p className="text-sm text-red-400 mt-2">{errors.password}</p>}
              </div>
              <div className="w-full flex flex-col gap-4 justify-center items-center">
                <button className="w-full py-3 rounded-lg text-base font-semibold text-white transition-all bg-blue-600 hover:bg-blue-700 hover:scale-105" type="submit">
                  {loading ? <Loader size="small" color="white" /> : "Create account"}
                </button>

                {!inviteToken && (
                  <div className="w-full flex justify-center">
                    <Link className="w-full py-3 rounded-lg text-base font-semibold text-white transition-all bg-gray-800 hover:bg-gray-700 text-center" to={`/auth/`}>
                      Already have an account
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Invite;
